import type { Ref } from 'vue'

/**
 * Tracks route navigation so a blocking cover can stand in for the thin top bar.
 *
 * Must never stay up after the router has settled: a stuck cover sits above the
 * storefront (`pointer-events: auto`) and eats every click — which looked like
 * "publish did nothing" / "weird borders" over an already-painted store.
 */
export function useRouteLoading(): Ref<boolean> {
  const loading = ref(false)
  if (!import.meta.client) return loading

  const nuxtApp = useNuxtApp()
  const router = useRouter()
  /** Bumps on each navigation so delayed hides cannot clear a newer show. */
  let navToken = 0
  let settleTimer: ReturnType<typeof setTimeout> | undefined
  let failSafe: ReturnType<typeof setTimeout> | undefined

  function show() {
    clearTimeout(settleTimer)
    clearTimeout(failSafe)
    loading.value = true
    // Hard cap — Suspense / page:finish can hang after publish → live.
    failSafe = setTimeout(hide, 3500)
  }

  function hide() {
    clearTimeout(settleTimer)
    clearTimeout(failSafe)
    loading.value = false
  }

  const stopBefore = router.beforeEach((to, from) => {
    if (to.fullPath === from.fullPath) return
    navToken += 1
    show()
  })

  const stopAfter = router.afterEach(() => {
    const token = navToken
    clearTimeout(settleTimer)
    // Router finished. Do not wait on page:finish — a hung Suspense used to
    // leave the fixed cover on top of the live store for up to 12s.
    settleTimer = setTimeout(() => {
      if (token === navToken) hide()
    }, 120)
  })

  const offStart = nuxtApp.hook('page:start', () => {
    show()
  })
  const offFinish = nuxtApp.hook('page:finish', hide)
  const offError = nuxtApp.hook('app:error', hide)

  onScopeDispose(() => {
    stopBefore()
    stopAfter()
    offStart()
    offFinish()
    offError()
    clearTimeout(settleTimer)
    clearTimeout(failSafe)
  })

  return loading
}
